import { cn } from '@/lib/utils';
import { Paperclip, Plus, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { FileSpec, useConfig } from '@chainlit/react-client';

import { Translator } from '@/components/i18n';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from '@/components/ui/tooltip';

import { useUpload } from '@/hooks/useUpload';

import { isReportMode } from '@/lib/embedded-sidebar';
import { podcastState } from '@/state/podcast';

import ResourceSelectionPanel from '../CustomChatConfiguration/DMResourceSelectionPanel';
import { useSourcesData } from '../CustomChatConfiguration/DMuseSourcesData';
import DMPodcastMenuItem from './DMPodcastMenuItem';
import DMSourcesSubmenu from './DMSourcesSubmenu';
import { usePodcastAccess } from './usePodcastAccess';

interface MoreConfigBtnProps {
    disabled?: boolean;
    fileSpec: FileSpec;
    onFileUpload: (files: File[]) => void;
    onFileUploadError: (error: string) => void;
    sourcesOpen?: boolean;
    onSourcesOpenChange?: (open: boolean) => void;
    onClose?: () => void;
}

const MoreConfigBtn = ({
    disabled = false,
    fileSpec,
    onFileUpload,
    onFileUploadError,
    sourcesOpen: externalSourcesOpen,
    onSourcesOpenChange: externalOnSourcesOpenChange,
    onClose
}: MoreConfigBtnProps) => {
    const { config } = useConfig();
    const podcast = useRecoilValue(podcastState);
    const [open, setOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Data hooks
    const {
        dataSources,
        selectedSources,
        isLoading: loadingSources,
        error: sourcesError,
        isSaving: savingSources,
        toggleSource,
        fetchSources
    } = useSourcesData();

    // Dialog states — owned here so they survive dropdown unmount
    const [sourcesDialogOpenInternal, setSourcesDialogOpenInternal] =
        useState(false);

    const sourcesDialogOpen =
        externalSourcesOpen !== undefined
            ? externalSourcesOpen
            : sourcesDialogOpenInternal;

    const handleSourcesDialogChange = useCallback(
        (nextOpen: boolean) => {
            if (externalOnSourcesOpenChange) {
                externalOnSourcesOpenChange(nextOpen);
            } else {
                setSourcesDialogOpenInternal(nextOpen);
            }
            if (!nextOpen && onClose) {
                setTimeout(() => onClose(), 100);
            }
        },
        [externalOnSourcesOpenChange, onClose]
    );

    // Ensure data is fetched once
    useEffect(() => {
        fetchSources();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const upload = useUpload({
        spec: fileSpec,
        onResolved: (payloads: File[]) => onFileUpload(payloads),
        onError: onFileUploadError,
        options: { noDrag: true }
    });

    const handleUploadClick = useCallback(
        (e: React.MouseEvent) => {
            if (upload) {
                const rootProps = upload.getRootProps();
                if (rootProps.onClick) {
                    rootProps.onClick(e as any);
                }
            }
            setOpen(false);
        },
        [upload]
    );

    const openSourcesDialog = useCallback(() => {
        setOpen(false);
        handleSourcesDialogChange(true);
    }, [handleSourcesDialogChange]);

    const closeDropdown = useCallback(() => setOpen(false), []);

    const handleTriggerPointerDown = useCallback(
        (event: React.PointerEvent<HTMLButtonElement>) => {
            if (event.button === 0 && !event.ctrlKey) {
                event.preventDefault();
            }
        },
        []
    );

    const handleTriggerClick = useCallback(() => {
        setOpen((prev) => !prev);
    }, []);

    const isUploadEnabled = config?.features.spontaneous_file_upload?.enabled;
    const isGenerating = podcast.isGenerating;
    const showUploadItem = isUploadEnabled && upload;
    const { getInputProps } = upload || {};

    // The report companion has nothing to offer here — uploads and podcasts
    // belong to the standalone chat — so the trigger never appears in report
    // mode. Embedded PLAIN chat is not report mode and keeps the standalone
    // behaviour, same as every other capability.
    //
    // Otherwise: nothing to offer this user — hide the trigger entirely instead
    // of opening onto an empty (or all-disabled) menu.
    const showPodcastItem = usePodcastAccess();
    const hasAnyMenuItem = showUploadItem || showPodcastItem;
    if (isReportMode || !hasAnyMenuItem) return null;

    return (
        <>
            {upload && (
                <input
                    ref={fileInputRef}
                    className="hidden"
                    {...(getInputProps ? getInputProps() : {})}
                />
            )}
            <DropdownMenu open={open} onOpenChange={setOpen}>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="inline-flex animate-in fade-in-0 zoom-in-95 duration-200">
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        id="more-config-btn"
                                        variant="tertiary"
                                        size="icon"
                                        disabled={disabled}
                                        className={cn(
                                            isGenerating && 'animate-pulse'
                                        )}
                                        onPointerDown={handleTriggerPointerDown}
                                        onClick={handleTriggerClick}
                                        onFocus={(e) => e.stopPropagation()}
                                    >
                                        {isGenerating ? (
                                            <RefreshCw className="animate-spin" />
                                        ) : (
                                            <Plus />
                                        )}
                                    </Button>
                                </DropdownMenuTrigger>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>
                                {isGenerating
                                    ? 'Generating podcast...'
                                    : 'More options'}
                            </p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <DropdownMenuContent
                    align="start"
                    side="top"
                    className="bg-background w-56"
                >
                    {showUploadItem && (
                        <DropdownMenuItem
                            onClick={handleUploadClick}
                            disabled={disabled}
                           
                        >
                            <Paperclip />
                            <span>
                                <Translator path="chat.input.actions.attachFiles" />
                            </span>
                        </DropdownMenuItem>
                    )}

                    {/* <DMSourcesSubmenu
                        dataSources={dataSources}
                        selectedSources={selectedSources}
                        onToggleSource={toggleSource}
                        onOpenDialog={openSourcesDialog}
                    /> */}

                    <DMPodcastMenuItem onCloseDropdown={closeDropdown} />
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Sources Dialog — rendered outside the dropdown so it survives unmount */}
            {/* <Dialog
                open={sourcesDialogOpen}
                onOpenChange={handleSourcesDialogChange}
            >
                <DialogContent
                    className="flex flex-col gap-0 bg-background shadow-xl p-0 border border-border w-[800px] max-w-[95vw] h-[70vh] max-h-[700px] overflow-hidden select-none"
                    onClick={(e) => e.stopPropagation()}
                >
                    <ResourceSelectionPanel
                        sources={dataSources}
                        selectedSources={selectedSources}
                        isLoading={loadingSources}
                        error={sourcesError}
                        disabled={savingSources}
                        onToggleSource={toggleSource}
                        onClose={() => handleSourcesDialogChange(false)}
                    />
                </DialogContent>
            </Dialog> */}

        </>
    );
};

export default MoreConfigBtn;
